import { useEffect, useState } from "react";
import Navigation from "./Navigation";
import GridBackground from "./GridBackground";
import "./css/DevOpsWorkItems.css";

interface WorkItem {
  id: number;
  rev: number;
  url: string;
  fields: {
    "System.Title": string;
    "System.State": string;
    "System.WorkItemType": string;
    "System.CreatedDate": string;
    "System.TeamProject": string;
    "System.Description"?: string;
    "System.AssignedTo"?: {
      displayName: string;
      uniqueName: string;
    };
  };
}

// Helper function to format ISO date to readable format
const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Helper function to construct Azure DevOps web UI URL
const getWorkItemUrl = (item: WorkItem): string => {
  // Extract org from the API URL
  const urlParts = item.url.match(/https:\/\/dev\.azure\.com\/([^\/]+)\//);
  const org = urlParts ? urlParts[1] : '';
  const project = item.fields["System.TeamProject"];
  const id = item.id;

  return `https://dev.azure.com/${org}/${project}/_workitems/edit/${id}`;
};

export default function DevOpsWorkItems() {
  const [items, setItems] = useState<WorkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<WorkItem | null>(null);

  useEffect(() => {
    fetch("/api/devops")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch work items");
        return res.json();
      })
      .then((data) => {
        setItems(data.value || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const openModal = (item: WorkItem) => {
    setSelectedItem(item);
  };

  const closeModal = () => {
    setSelectedItem(null);
  };

  return (
    <>
      <GridBackground />
      <Navigation />
      <div className="devops-container">
        {loading && <div className="loading">Loading work items...</div>}
        {error && <div className="error">Error: {error}</div>}

        {!loading && !error && (
          <>
            <h2>Azure DevOps Work Items</h2>
            <div className="table-wrapper">
              <table className="work-items-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Type</th>
                    <th>State</th>
                    <th>Assigned To</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.fields["System.Title"]}</td>
                      <td>{item.fields["System.WorkItemType"]}</td>
                      <td>
                        <span className={`state-badge state-${item.fields["System.State"].toLowerCase().replace(" ", "-")}`}>
                          {item.fields["System.State"]}
                        </span>
                      </td>
                      <td>
                        {item.fields["System.AssignedTo"]?.displayName || "Unassigned"}
                      </td>
                      <td>{formatDate(item.fields["System.CreatedDate"])}</td>
                      <td className="actions-cell">
                        <button
                          onClick={() => openModal(item)}
                          className="description-btn"
                          title="See details"
                        >
                          🔍
                        </button>
                        <a
                          href={getWorkItemUrl(item)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="view-link"
                          title="Go to DevOps Boards"
                        >
                          View in DevOps
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {items.length === 0 && (
              <p className="no-items">No work items found.</p>
            )}
          </>
        )}
      </div>

      {/* Modal for viewing description */}
      {selectedItem && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                #{selectedItem.id} - {selectedItem.fields["System.Title"]}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="work-item-meta">
                <span className="meta-item">
                  <strong>Type:</strong> {selectedItem.fields["System.WorkItemType"]}
                </span>
                <span className="meta-item">
                  <strong>State:</strong> {selectedItem.fields["System.State"]}
                </span>
                <span className="meta-item">
                  <strong>Assigned To:</strong>{" "}
                  {selectedItem.fields["System.AssignedTo"]?.displayName || "Unassigned"}
                </span>
              </div>
              <div className="description-section">
                <h4>Description</h4>
                <div className="description-content">
                  {selectedItem.fields["System.Description"] ? (
                    <div dangerouslySetInnerHTML={{ __html: selectedItem.fields["System.Description"] }} />
                  ) : (
                    <p className="no-description">No description available</p>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <a
                href={getWorkItemUrl(selectedItem)}
                target="_blank"
                rel="noopener noreferrer"
                className="modal-devops-link"
              >
                Open in Azure DevOps →
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
